// External imports
import React, { useEffect, useRef } from 'react';
import { AppBar, Button, Paper, Toolbar, Grid, withStyles } from '@material-ui/core';
import SignaturePad from 'react-signature-pad-wrapper';
// Internal imports
import {
  toBMPDataURL,
  sigTypes,
  scaleCanvasPixelRatio,
  canvasSize,
  canvasOptions,
  resizeCanvas,
  isCanvasScaled,
  cleanUpCanvas,
} from '~/app/bmpConversionHelpers.js';
import { getSigneeTitle } from '~/app/Pages/Returns/components/helpers/modalHelpers.js';
import ErrorHelpers from '~/app/errorHelpers.js';
import SimpleDialog from '#/Common/SimpleDialog.jsx';
import { useSetState } from '~/app/Utility/customHooks';
// Redux imports
import { useDispatch } from 'react-redux';
import { actions as appActions } from '~/app/redux/modules/app';
import { Typography } from '~/node_modules/@material-ui/core/index';
// Styling imports
import { styles } from '~/app/Pages/Returns/css/formViewerModals.js';

const initialState = {
  isConsentModalOpen: false,
  hasConsented: false,
};

const SignatureCapture = props => {
  const { classes } = props;
  const dispatch = useDispatch();
  const signaturePad = useRef();
  const [state, setState] = useSetState({
    isConsentModalOpen: false,
    hasConsented: false, // We only use this for disabling logic incase the user is a smarty and uses DEV Tools to remove the consent modal
  });

  useEffect(() => {
    if (signaturePad.current && signaturePad.current.canvas != null) {
      scaleCanvasPixelRatio(signaturePad.current.canvas);
    }

    // check to see if a consent should be displayed
    handleShouldShowConsent();

    return () => {
      if (isCanvasScaled) {
        cleanUpCanvas();
      }
    };
  }, []);

  // If there are multiple signatures we need to reset the state to show for the next signee
  useEffect(() => {
    setState({ ...initialState });
    handleShouldShowConsent();
  }, [props.currentSig]);

  const handleShouldShowConsent = () => {
    if (
      props.consent &&
      [1, 2, 7].includes(parseInt(props.currentSig)) // only tp+sp+officer/business are required to consent
    ) {
      setState({ isConsentModalOpen: true });
    } else {
      // If no consent was supplied, then we should allow them to sign
      setState({ hasConsented: true });
    }
  };

  const handleCancel = e => {
    e.preventDefault();
    // Close and clear the modal
    signaturePad.current.clear();
    props.handleClose();
  };

  const clearSignature = e => {
    e.preventDefault();
    signaturePad.current.clear();
  };

  const acceptSignature = async e => {
    e.preventDefault();
    const haveSignature = signaturePad.current.isEmpty();
    const onScreen = 1;

    if (!haveSignature) {
      if (props.convertString === false) {
        const signatureDataOne = isCanvasScaled
          ? resizeCanvas(signaturePad.current.canvas).toDataURL()
          : signaturePad.current.toDataURL(); // Getting signature data (base 64)
        // Close and clear the modal
        signaturePad.current.clear();
        props.handleSignatureCapture(sigTypes[props.currentSig], signatureDataOne, onScreen);
      } else {
        const signaturePadCanvas = isCanvasScaled
          ? resizeCanvas(signaturePad.current.canvas)
          : signaturePad.current.canvas;
        const padType = 'screen';
        let sigString = '';

        try {
          sigString = await toBMPDataURL(
            signaturePadCanvas,
            padType,
            'hardcoded "screen"',
            'acceptSignature',
          );
        } catch (error) {
          console.log('toBMPDataURL - acceptSignature - error: ', error);
          ErrorHelpers.handleError('Error formatting signature 3', error);
        }

        sigString = sigString.replace('data:image/bmp;base64,', ''); // strip url src info

        // Close and clear the modal
        signaturePad.current.clear();

        props.handleSignatureCapture(sigTypes[props.currentSig], sigString, onScreen);
      }
    } else {
      dispatch(
        appActions.showSnackbarMessage(
          "No signature captured. Please sign and then click on the 'Accept' button",
          'error',
          3500,
          {
            vertical: 'top',
            horizontal: 'center',
          },
        ),
      );
    }
  };

  const toolbarTitle = getSigneeTitle(props.currentSig);

  return (
    <Paper className={classes.signatureCaptureContainer}>
      <AppBar position="static">
        <Toolbar className={classes.toolBar}>
          <Typography className={classes.toolBarTitle}>{toolbarTitle}</Typography>
        </Toolbar>
      </AppBar>
      <div className={classes.signatureCaptureForm}>
        <Grid container spacing={16} justify="flex-start">
          <Grid item xs={12}>
            <Typography variant="body1">Please sign below</Typography>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.signatureCapturePad}>
              <SignaturePad
                width={canvasSize.width}
                height={canvasSize.height}
                ref={ref => (signaturePad.current = ref)}
                options={{ ...canvasOptions }}
              />
            </div>
          </Grid>
          <Grid item xs={12} className={classes.signatureCaptureFooter}>
            <Button
              className={classes.marginRight}
              variant="outlined"
              id="btnCancelSignDoc"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              className={classes.marginRight}
              variant="outlined"
              id="btnClearSignDoc"
              onClick={clearSignature}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              color="primary"
              id="btnAcceptSignDoc"
              onClick={acceptSignature}
              disabled={!state.hasConsented}
            >
              Accept
            </Button>
          </Grid>
        </Grid>
      </div>

      {/* This appears for each signee, does not popup if sending the RMS - that is handled in another component when the user signs */}
      {state.isConsentModalOpen && (
        <SimpleDialog
          open={state.isConsentModalOpen}
          confirmText="OK"
          cancelText="Cancel"
          styled={true}
          onConfirm={() => {
            setState({ isConsentModalOpen: false, hasConsented: true });
          }}
          onClose={e => {
            handleCancel(e);
            setState({ isConsentModalOpen: false });
          }}
          dialogTitle="Signature Disclosure Agreement"
          contentText={props.consent}
          isConsent={true}
        />
      )}
    </Paper>
  );
};

export default withStyles(styles)(SignatureCapture);
